/**
 * Sign-out, for the profile's account section (T51).
 *
 * It goes through the session controller (T11): with unsynced workout writes
 * in the outbox it refuses and reports how many, the person confirms
 * (plan D23), and only then is the queue dropped, `ApiClient.signOut` called
 * and the Query cache and the persisted cache cleared. The session context
 * then flips to signed out and the gate shows sign-in (this device has held
 * a session, `D262`), with its door to the intake.
 *
 * The URL is reset to `/` afterwards, so whoever signs in next does not land
 * inside the previous person's open overlay.
 */
import { forgetGuestIntake } from "@repo/client/onboarding";
import { useNavigate } from "@tanstack/react-router";
import { useCallback, useState } from "react";
import { useSession } from "../../data/session";
import { intakeStore } from "../onboarding/storage";

export interface SignOutState {
  /** Close the confirmation without signing out. */
  cancel(): void;
  /** Sign out, losing the unsynced writes the confirmation named. */
  confirm(): void;
  /** Unsynced writes awaiting the person's answer, or `null` when not asking. */
  readonly needsConfirm: number | null;
  readonly pending: boolean;
  /** Start: signs out at once, or opens the confirmation. */
  signOut(): void;
}

/**
 * Answers kept from before a sign-in whose account's progress was never read
 * (`D262`): they were this person's, and the next one to sign in here must
 * not adopt them. Not in the per-account cleanup, which also runs when a
 * DIFFERENT account signs in — the very moment a guest's answers must
 * survive. `forgetGuestIntake` never rejects.
 */
function forgetGuestAnswers(): void {
  forgetGuestIntake(intakeStore);
}

export function useSignOut(): SignOutState {
  const session = useSession();
  const navigate = useNavigate();
  const [needsConfirm, setNeedsConfirm] = useState<number | null>(null);
  const [pending, setPending] = useState(false);

  const run = useCallback(
    (force: boolean) => {
      setPending(true);
      session
        .signOut({ force })
        .then((result) => {
          if (result.needsConfirm) {
            setNeedsConfirm(result.pending);
            return;
          }
          setNeedsConfirm(null);
          forgetGuestAnswers();
          return navigate({ replace: true, search: {}, to: "/" });
        })
        .catch((error: unknown) => {
          console.error("[auth] sign-out failed", error);
        })
        .finally(() => {
          setPending(false);
        });
    },
    [navigate, session]
  );

  const signOut = useCallback(() => run(false), [run]);
  const confirm = useCallback(() => run(true), [run]);
  const cancel = useCallback(() => setNeedsConfirm(null), []);

  return { cancel, confirm, needsConfirm, pending, signOut };
}
